(() => {
  const boundForms = new WeakSet();

  const liveStateOwner = (element) => {
    if (!element) return null;
    const form = element.matches?.('form') ? element : element.form || element.closest?.('form');
    const formOwner = form?.querySelector?.('buy-buttons[data-av-compatibility-state]');
    if (formOwner) return formOwner;
    const closestOwner = element.closest?.('[data-av-compatibility-state]');
    if (closestOwner?.matches?.('form, product-form')) {
      return closestOwner.querySelector?.('buy-buttons[data-av-compatibility-state]') || closestOwner;
    }
    return closestOwner || null;
  };

  const stateFor = (element) => liveStateOwner(element)?.dataset.avCompatibilityState || 'not-required';

  const relatedOwners = (form) => {
    const owners = new Set(form?.querySelectorAll?.('[data-av-compatibility-state]') || []);
    if (form?.matches?.('[data-av-compatibility-state]')) owners.add(form);
    const productForm = form?.closest?.('product-form[data-av-compatibility-state]');
    if (productForm) owners.add(productForm);
    if (form?.id) {
      const escapedId = window.CSS?.escape ? window.CSS.escape(form.id) : form.id;
      document.querySelectorAll(`[form="${escapedId}"][data-av-compatibility-state]`).forEach((owner) => owners.add(owner));
    }
    return owners;
  };

  const purchaseControls = (form) => {
    const controls = new Set(form?.querySelectorAll?.('[data-av-atc-control]') || []);
    if (form?.id) {
      const escapedId = window.CSS?.escape ? window.CSS.escape(form.id) : form.id;
      document.querySelectorAll(`[form="${escapedId}"][data-av-atc-control]`).forEach((control) => controls.add(control));
    }
    return controls;
  };

  const selectedVariantFor = (form) => {
    if (!form?.id) return undefined;
    const picker = Array.from(document.querySelectorAll('variant-picker[form-id]'))
      .find((element) => element.getAttribute('form-id') === form.id);
    const variantJson = picker?.querySelector('script[data-variant]')?.textContent;
    if (!variantJson) return undefined;
    try {
      const variant = JSON.parse(variantJson);
      return variant?.id ? variant : null;
    } catch {
      return undefined;
    }
  };

  const syncNativeAvailability = (form, variant) => {
    if (!form || variant === undefined) return false;
    const available = variant?.available === true;

    purchaseControls(form).forEach((control) => {
      const labels = control.closest('buy-buttons');
      const label = variant == null
        ? labels?.dataset.avAtcLabelUnavailable
        : available
          ? labels?.dataset.avAtcLabelAvailable
          : labels?.dataset.avAtcLabelSoldOut;

      if (available) {
        if (control.hasAttribute('data-av-inventory-disabled')) {
          control.disabled = false;
          control.removeAttribute('aria-disabled');
          control.removeAttribute('data-av-inventory-disabled');
          if (label) control.textContent = label;
        }
      } else {
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
        control.setAttribute('data-av-inventory-disabled', '');
        if (label) control.textContent = label;
      }
    });

    relatedOwners(form).forEach((owner) => {
      owner.querySelectorAll?.('[data-av-payment-wrapper]').forEach((wrapper) => {
        if (available) {
          if (!wrapper.hasAttribute('data-av-inventory-hidden')) return;
          wrapper.removeAttribute('hidden');
          wrapper.removeAttribute('inert');
          wrapper.removeAttribute('aria-hidden');
          wrapper.removeAttribute('data-av-inventory-hidden');
        } else {
          wrapper.setAttribute('hidden', '');
          wrapper.setAttribute('inert', '');
          wrapper.setAttribute('aria-hidden', 'true');
          wrapper.setAttribute('data-av-inventory-hidden', '');
        }
      });
    });

    return true;
  };

  const preserveNativeAvailability = () => true;

  const beginTransition = (form) => {
    if (!form) return false;
    relatedOwners(form).forEach((owner) => {
      owner.dataset.avCompatibilityTransition = 'true';
    });
    return true;
  };

  const syncForm = (form) => {
    if (!form) return false;
    const source = form.querySelector?.('buy-buttons[data-av-compatibility-state]');
    const state = source?.dataset.avCompatibilityState || form.dataset.avCompatibilityState || 'not-required';
    relatedOwners(form).forEach((owner) => {
      const localSource = owner.matches?.('buy-buttons[data-av-compatibility-state]')
        ? owner
        : owner.querySelector?.('buy-buttons[data-av-compatibility-state]');
      owner.dataset.avCompatibilityState = localSource?.dataset.avCompatibilityState || state;
      owner.removeAttribute('data-av-compatibility-transition');
    });
    return true;
  };

  const bindForm = (form) => {
    if (!form || boundForms.has(form)) return;
    boundForms.add(form);
    form.addEventListener('product:rerender', () => queueMicrotask(() => syncForm(form)));
    syncForm(form);
    syncNativeAvailability(form, selectedVariantFor(form));
  };

  const bindForms = (root = document) => {
    if (root.matches?.('form[data-av-compatibility-state]')) bindForm(root);
    root.querySelectorAll?.('form[data-av-compatibility-state]').forEach(bindForm);
  };

  document.addEventListener('change', (event) => {
    const control = event.target;
    if (!control?.matches?.('[data-option-position]')) return;
    beginTransition(control.form || control.closest?.('form'));
  }, true);

  document.addEventListener('avontic:compatibility-changing', (event) => {
    beginTransition(event.target?.matches?.('form') ? event.target : event.target?.closest?.('form'));
  }, true);

  document.addEventListener('variant:change', (event) => {
    const form = event.target?.matches?.('form') ? event.target : event.target?.closest?.('form');
    if (!form) return;
    const variant = event.detail?.variant;
    syncNativeAvailability(form, variant);
    queueMicrotask(() => {
      syncForm(form);
      syncNativeAvailability(form, variant);
    });
  }, true);

  window.addEventListener('popstate', () => {
    document.querySelectorAll('form[data-av-compatibility-state]').forEach(beginTransition);
  });

  const ready = () => {
    bindForms();
    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) bindForms(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();

  window.AvonticCompatibilityGate = Object.freeze({
    version: '4.1.0',
    beginTransition,
    keepAddToCartAvailable: preserveNativeAvailability,
    preserveNativeAvailability,
    syncNativeAvailability,
    stateFor,
    syncForm
  });
})();
